import { useMemo, useEffect } from 'react';
import { CheckCircle, ChevronDown, FileText, Video } from 'lucide-react';
import VideoThumbnail from '../Prompts/VideoThumbnail';
import { mediaUrl } from '../Prompts/promptsV2Utils';
import { isPersistedMediaUrl } from './draftPayload';
import type { ScreeningPromptItem } from './types';

type OnboardingPromptCardProps = {
  index: number;
  item: ScreeningPromptItem;
  isExpanded: boolean;
  videoFile?: File;
  hasDescription?: boolean;
  hasDocument?: boolean;
  compact?: boolean;
  onToggle: (index: number) => void;
  onEdit: (index: number) => void;
};

export default function OnboardingPromptCard({
  index,
  item,
  isExpanded,
  videoFile,
  hasDescription = false,
  hasDocument = false,
  compact = false,
  onToggle,
  onEdit,
}: OnboardingPromptCardProps) {
  const remoteUrl = isPersistedMediaUrl(item.fileUrl) ? mediaUrl(item.fileUrl) : '';

  const localPreviewUrl = useMemo(() => {
    // Only use a blob preview while the S3 upload is still in progress.
    if (remoteUrl || !videoFile) return '';
    return URL.createObjectURL(videoFile);
  }, [remoteUrl, videoFile]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const resolvedVideoUrl = remoteUrl || localPreviewUrl;
  const hasVideo = Boolean(resolvedVideoUrl);
  const responseCount = [hasVideo, hasDescription, hasDocument].filter(Boolean).length;

  return (
    <div
      className={`ssr-prompt-accordion ${isExpanded ? 'is-expanded' : ''} ${compact ? 'is-compact' : ''}`}
    >
      <button
        type="button"
        className={`ssr-video-q ${compact ? 'ssr-video-q--sidebar' : ''}`}
        onClick={() => onToggle(index)}
        aria-expanded={isExpanded}
      >
        {compact ? (
          <>
            <div className="ssr-video-q-sidebar-head">
              <div className="ssr-video-q-sidebar-left">
                <span className="ssr-video-q-number">{index + 1}</span>
                <Video size={14} className="ssr-video-q-icon" />
              </div>
              <div className="ssr-video-q-sidebar-right">
                {responseCount > 0 ? (
                  <span className="ssr-video-q-badge">
                    <CheckCircle size={12} /> {responseCount}
                  </span>
                ) : (
                  <span className="ssr-video-q-badge ssr-video-q-badge--muted">Optional</span>
                )}
                <ChevronDown size={16} className="ssr-video-q-chevron" aria-hidden />
              </div>
            </div>
            <span className="ssr-video-q-text">{item.content}</span>
          </>
        ) : (
          <>
            <span className="ssr-video-q-number">{index + 1}</span>
            <Video size={16} className="ssr-video-q-icon" />
            <span className="ssr-video-q-text">{item.content}</span>
            {responseCount > 0 ? (
              <span className="ssr-video-q-badge">
                <CheckCircle size={12} /> {responseCount} response{responseCount === 1 ? '' : 's'}
              </span>
            ) : (
              <span className="ssr-video-q-badge ssr-video-q-badge--muted">Optional</span>
            )}
            <ChevronDown size={18} className="ssr-video-q-chevron" aria-hidden />
          </>
        )}
      </button>

      {!compact && isExpanded ? (
        <div className="ssr-prompt-accordion-body">
          {hasVideo ? (
            <div className="ssr-prompt-preview">
              <VideoThumbnail src={resolvedVideoUrl} onPlay={() => onEdit(index)} />
              <div className="ssr-prompt-preview-copy">
                <p className="ssr-prompt-preview-title">Video response attached</p>
                <p className="ssr-prompt-preview-sub">
                  Add a description or supporting document to strengthen this answer.
                </p>
              </div>
            </div>
          ) : (
            <p className="ssr-prompt-panel-lede">
              Record a short answer, add a written description, or attach a supporting document. All
              responses are optional but help recruiters get to know you faster.
            </p>
          )}

          <div className="ssr-prompt-meta-row">
            {hasDescription ? (
              <span className="ssr-prompt-meta-chip">
                <FileText size={14} /> Description added
              </span>
            ) : null}
            {hasDocument ? (
              <span className="ssr-prompt-meta-chip">
                <FileText size={14} /> Document attached
              </span>
            ) : null}
          </div>

          <div className="ssr-prompt-panel-actions">
            <button type="button" className="ssr-record-btn" onClick={() => onEdit(index)}>
              Edit prompt
            </button>
          </div>
        </div>
      ) : null}

      {compact && isExpanded ? (
        <div className="ssr-prompt-accordion-body ssr-prompt-accordion-body--compact">
          <div className="ssr-prompt-panel-actions">
            <button type="button" className="ssr-record-btn" onClick={() => onEdit(index)}>
              {hasVideo ? 'Edit response' : 'Record answer'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
